import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import styles from "./TrainingSummary.module.css";

/**
 * Prev / next controls for exercises within the same training session.
 */
export default function TrainingSummaryExerciseNav({
  exercises = [],
  currentTeId,
  currentLabel,
  onSelectExercise,
  loading = false,
}) {
  const { t } = useTranslation();
  const list = Array.isArray(exercises) ? exercises : [];
  const total = list.length;
  const index = list.findIndex((ex) => String(ex?.te_id) === String(currentTeId));
  const hasIndex = index >= 0;
  const position = hasIndex ? index + 1 : null;
  const prevEx = hasIndex && index > 0 ? list[index - 1] : null;
  const nextEx =
    hasIndex && index < total - 1 ? list[index + 1] : null;

  if (loading) {
    return (
      <div className={styles.exerciseNav} aria-busy="true">
        <span className={styles.exerciseNavLoading}>
          {t("loading") || "Loading…"}
        </span>
      </div>
    );
  }

  if (total <= 1) {
    return (
      <div className={styles.exerciseNav}>
        <div className={styles.exerciseNavCenter}>
          <span className={styles.exerciseNavPos}>
            {total === 1
              ? t("training_summary_one_exercise") || "1 exercise in session"
              : t("training_summary_exercise_nav") || "Exercise"}
          </span>
          {currentLabel ? (
            <span className={styles.exerciseNavName}>{currentLabel}</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.exerciseNav}>
      <button
        type="button"
        className={styles.exerciseNavBtn}
        disabled={!prevEx}
        onClick={() => prevEx && onSelectExercise?.(prevEx)}
        aria-label={t("training_summary_prev_exercise") || "Previous exercise"}
      >
        <ChevronLeft size={20} aria-hidden />
        <span className={styles.exerciseNavBtnLabel}>
          {t("previous") || "Previous"}
        </span>
      </button>

      <div className={styles.exerciseNavCenter}>
        <span className={styles.exerciseNavPos}>
          {position != null
            ? t("training_summary_exercise_position", {
                current: position,
                total,
              }) || `Exercise ${position} / ${total}`
            : t("training_summary_exercise_nav") || "Exercise"}
        </span>
        {currentLabel ? (
          <span className={styles.exerciseNavName} title={currentLabel}>
            {currentLabel}
          </span>
        ) : null}
        {total > 2 && hasIndex ? (
          <label className={styles.exerciseNavJump}>
            <span className={styles.exerciseNavJumpLabel}>
              {t("jumpTo") || "Jump to"}
            </span>
            <select
              className={styles.exerciseNavSelect}
              value={String(currentTeId || "")}
              onChange={(e) => {
                const te = e.target.value;
                const ex = list.find((x) => String(x?.te_id) === te);
                if (ex) onSelectExercise?.(ex);
              }}
            >
              {list.map((ex, i) => (
                <option key={ex.te_id} value={String(ex.te_id)}>
                  {`${i + 1}. ${
                    ex.ex_name ||
                    ex.te_id?.slice(-6) ||
                    t("exercise") ||
                    "Exercise"
                  }`}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <button
        type="button"
        className={styles.exerciseNavBtn}
        disabled={!nextEx}
        onClick={() => nextEx && onSelectExercise?.(nextEx)}
        aria-label={t("training_summary_next_exercise") || "Next exercise"}
      >
        <span className={styles.exerciseNavBtnLabel}>
          {t("next") || "Next"}
        </span>
        <ChevronRight size={20} aria-hidden />
      </button>
    </div>
  );
}
